import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { jwtDecode, JwtPayload } from 'jwt-decode';

// Importaciones de entornos.
import { environment } from '../../environments/environment';

/**
 * Servicio de autenticación para gestionar el inicio de sesión, cierre de sesión
 * y verificación del estado del usuario autenticado.
 *
 * Este servicio se proporciona a nivel global (`providedIn: 'root'`), por lo que está
 * disponible en toda la aplicación como un singleton.
 *
 * Normalmente se encarga de funciones como almacenar/eliminar tokens, validar sesiones,
 * obtener información del usuario autenticado, entre otras tareas relacionadas con la autenticación.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /**
   * URL del endpoint para la autenticación.
   *
   * Esta propiedad almacena la URL a la que se enviarán las solicitudes HTTP
   * para realizar el proceso de autenticación de usuarios en la aplicación.
   */
  private url: string = '/auth';

  /**
   * Constructor de la clase AuthService.
   *
   * Inyecta el servicio `HttpClient`. El servicio `HttpClient` se
   * utiliza para realizar solicitudes HTTP al servidor de autenticación.
   *
   * @param http - Servicio de Angular para realizar solicitudes HTTP.
   */
  constructor(private http: HttpClient) {}

  /**
   * Realiza el proceso de inicio de sesión del empleado.
   *
   * Envía una solicitud POST con las credenciales (nombre de usuario y contraseña) al servidor de autenticación.
   * Si la autenticación es exitosa, guarda el token en el almacenamiento local (localStorage) para su uso posterior.
   *
   * @param username - El nombre de usuario del empleado para la autenticación.
   * @param password - La contraseña del empleado para la autenticación.
   *
   * @returns Una promesa que resuelve con los datos del empleado,
   *          incluyendo el token de autenticación, si la solicitud es exitosa.
   */
  login(username: string, password: string): Promise<any> {
    return firstValueFrom(
      this.http
        .post(`${environment.apiUrl + this.url}/login`, {
          username,
          password,
        })
        .pipe(
          map((authResponse: any) => {
            localStorage.setItem('token', authResponse.token);
            return authResponse;
          })
        )
    );
  }

  /**
   * Obtiene el token almacenado en el local storage.
   *
   * @returns Devuelve el token como cadena de texto.
   * Si no existe, devuelve una cadena vacía.
   */
  getToken(): string {
    const token = localStorage.getItem('token');
    return token ? token : '';
  }

  /**
   * Verifica si el token es válido.
   *
   * Comprueba si existe un token en el almacenamiento local (local storage).
   * Si existe el token se comprueba que sea válido y que no haya caducado.
   *
   * @param token - El token como cadena de texto.
   *
   * @returns Devuelve true si el token es válido, o false si no lo es.
   */
  isValidToken(token: string): boolean {
    if (!token) {
      return false;
    }

    try {
      const payload: JwtPayload = jwtDecode(token);

      if (payload.exp && payload.exp < Date.now() / 1000) {
        if (!environment.production) {
          console.warn('El token ha caducado.');
        }
        return false;
      }

      return true;
    } catch (error: any) {
      if (!environment.production) {
        console.error('Error en la descodificación del token:', error);
      }

      return false;
    }
  }

  /**
   * Elimina el token del local storage.
   */
  removeToken(): void {
    localStorage.removeItem('token');
  }
}
